<template>
	<div class="content">
		<section
			id="hero"
			class="container-expanded container band"
		>
			<h2>The Partner Portal</h2>
		</section>

		<section class="band">
			<div class="cols">
				<div class="col col-2"></div>
				<div class="col col-8">
					<AuthorisationComponent
						v-if="!hideOverrideBox"
						:allow-ef="true"
						:in-pil="true"
					>
						<p>
							<strong>Override User Type for viewing/testing:</strong>
						</p>
						<select @change="(e) => setUserType((e.target as HTMLSelectElement).value)">
							<option
								v-for="(v, k) in accountTypes"
								:key="k"
								:value="v"
							>
								{{ v }}
							</option>
						</select>
						<p>
							You can choose an override user type for viewing and testing purposes, and then
							<a
								href=""
								@click.prevent="hideOverrideBox = true"
								>click here</a
							>
							to hide this box. Refresh the page to reset the override, see the box again, and change the override.
						</p>

						<ul>
							<li>isChampion: {{ isChampion ? 'Champion' : 'no' }}</li>
							<li>isTeacher: {{ isTeacher ? 'Teacher' : 'no' }}</li>
							<li>isEf: {{ isEf ? 'Expert Facilitator' : 'no' }}</li>
							<li>isAdmin: {{ isAdmin ? 'Admin' : 'no' }}</li>
							<li>accountType: {{ accountType }}</li>
						</ul>
					</AuthorisationComponent>

					<template v-if="accountType === accountTypes.TeacherIndividual">
						<h2>Welcome {{ firstname }}</h2>
						<p>You’re now officially inside the <strong>Partner Portal</strong>.</p>
						<p>The Partner Portal is your go-to HQ for transforming culture in your school community. From here, you’ll dive into a growing bank of Resources, and grab the tools you, your teachers and your parents need to create meaningful, lasting change.</p>
					</template>

					<template v-if="accountType === accountTypes.ChampionIndividual">
						<h2>Welcome, {{ firstname }}!</h2>
						<p>You’re now officially inside the <strong>Partner Portal</strong>.</p>
						<p>The Partner Portal is your go-to HQ for transforming culture in your school community. From here, you’ll access your Dashboard and dive into a growing bank of Resources, and grab the tools you, your teachers and your parents need to create meaningful, lasting change.</p>
						<p>
							Champion accounts have access some things that regular teacher accounts do not. Mainly including view and edit access to the
							<router-link
								:to="{
									name: 'dashboard',
								}"
								title="Dashboard"
							>
								Dashboard</router-link
							>.
						</p>
					</template>

					<template v-if="accountType === accountTypes.ExpertFacilitator">
						<h2>Welcome, {{ firstname }}!</h2>
						<p>You’re now officially inside the <strong>Partner Portal</strong> and logged in as an Expert Facilitator.</p>

						<p v-if="school?.post_title">You're logged in to {{ school.post_title }}.</p>
						<p v-else>You don't currently have a school selected.</p>

						<p>
							To see a different school, go to
							<router-link
								:to="{
									name: 'schoolList',
								}"
							>
								schools
							</router-link>
							in the main nav, and choose a school.
						</p>
					</template>

					<template v-if="accountType === accountTypes.Admin">
						<h2>Welcome, {{ firstname }}!</h2>
						<p>You’re now officially inside the <strong>Partner Portal</strong> and logged in as an Admin.</p>
					</template>

					<template v-if="canViewDetails">
						<h4>Your details</h4>
						<p v-if="school?.post_title">
							You're logged in to <strong>{{ school.post_title }}</strong
							>.
						</p>
						<form
							action="#"
							class="pil"
							@submit.prevent="handleUpdateUserName"
						>
							<div class="cols cols-gutters">
								<div class="form-group col col-6">
									<label for="firstname">First name</label>
									<input
										id="firstname"
										type="text"
										autocomplete="given-name"
										v-model="user.firstname"
									/>
								</div>
								<div class="form-group col col-6">
									<label for="lastname">Last name</label>
									<input
										id="lastname"
										type="text"
										autocomplete="family-name"
										v-model="user.lastname"
									/>
								</div>
							</div>
							<div class="cols cols-gutters">
								<div class="form-group col col-8">
									<label for="email">Email</label>
									<input
										id="email"
										type="email"
										:value="email"
										disabled
										title="Contact your Champion to update your email."
									/>
								</div>
								<div class="form-group col col-4 justify-end align-end">
									<button
										class="btn"
										type="submit"
									>
										Update
									</button>
								</div>
							</div>
						</form>

						<h4>Change your password</h4>
						<form
							action="#"
							class="pil"
							@submit.prevent="handleUpdateUserPassword"
						>
							<!--
								A password manager keys a saved credential on a username field in
								the same form, and this form has none of its own — the email sits
								in the details form above. Without it there is nothing for the
								manager to attach the new password to, so it won't reliably offer
								to update the one it already has for this site.

								display:none, which is the shape Chromium documents for a change
								password form, rather than a visually-hidden clip. The field is an
								enhancement: a manager that ignores unrendered fields just doesn't
								get the hint, which is where we were before it existed. That beats
								carrying a real, laid-out field nobody can see. `readonly` because
								nobody fills it in either way.
							-->
							<input
								class="username-for-password-manager"
								type="email"
								autocomplete="username"
								:value="email"
								readonly
							/>

							<div class="cols cols-gutters">
								<div class="col col-6 form-group">
									<label for="password">New password</label>
									<input
										id="password"
										type="password"
										autocomplete="new-password"
										v-model="user.password"
										placeholder="*********"
									/>
								</div>
								<div class="col col-6 form-group">
									<label for="passwordConfirm">Confirm new password</label>
									<input
										id="passwordConfirm"
										type="password"
										autocomplete="new-password"
										v-model="user.passwordConfirm"
									/>
								</div>
							</div>
							<div class="cols cols-gutters">
								<div class="col col-6 form-group">
									<p
										class="error"
										v-if="passwordError"
									>
										{{ passwordError }}
									</p>
								</div>
								<div class="col col-6 form-group justify-end align-end">
									<button
										class="btn"
										type="submit"
										:disabled="!!passwordError || !user.password || !user.passwordConfirm"
									>
										Update Password
									</button>
								</div>
							</div>
						</form>
					</template>
				</div>
			</div>
			<LoadingComponent
				v-if="loading"
				:loading="true"
				:full-screen="true"
			/>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import bookImage from '@/images/book.jpg';
import { useUser } from '@/stores/userStore';
import { mapActions, mapState } from 'pinia';
import AuthorisationComponent from '@/components/shared/AuthorisationComponent.vue';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';

export default defineComponent({
	components: {
		AuthorisationComponent,
		LoadingComponent,
	},
	data() {
		return {
			bookImage,
			championView: true,
			hidePil: false,
			accountType: '',
			accountTypes: {
				None: 'None',
				TeacherIndividual: 'Teacher Individual',
				ChampionIndividual: 'Champion Individual',
				ExpertFacilitator: 'Expert Facilitator',
				Operations: 'Operations',
				Admin: 'Admin',
			},
			user: {
				firstname: '',
				lastname: '',
				password: '',
				passwordConfirm: '',
			},
			loading: false,
			hideOverrideBox: false,
		};
	},
	computed: {
		baseUrl() {
			return window.location.origin;
		},
		passwordError() {
			if (!this.user.password && !this.user.passwordConfirm) {
				return '';
			}

			if (this.user.password !== this.user.passwordConfirm) {
				return 'Passwords do not match';
			}

			if (this.user.password.length < 8) {
				return 'Password must be at least 8 characters long';
			}

			return '';
		},
		canViewDetails() {
			return this.accountType === this.accountTypes.TeacherIndividual || this.accountType === this.accountTypes.ChampionIndividual || this.accountType === this.accountTypes.ExpertFacilitator || this.accountType === this.accountTypes.Operations || this.accountType === this.accountTypes.Admin;
		},
		...mapState(useUser, [
			'isChampion',
			'school',
			'email',
			'username',
			'firstname',
			'lastname',
			'isEf',
			'isOperations',
			'isAdmin',
			'isTeacher',
		]),
	},
	mounted() {
		this.user.firstname = this.firstname;
		this.user.lastname = this.lastname;
	},
	beforeMount() {
		this.setUserType();
	},
	methods: {
		setUserType(value: string = '') {
			if (value) {
				this.accountType = value;
				return;
			}

			if (this.isAdmin) {
				this.accountType = this.accountTypes.Admin;
				return;
			}

			if (this.isEf) {
				this.accountType = this.accountTypes.ExpertFacilitator;
				return;
			}

			if (this.isOperations) {
				this.accountType = this.accountTypes.Operations;
				return;
			}

			if (this.isChampion) {
				this.accountType = this.accountTypes.ChampionIndividual;
				return;
			}

			if (this.isTeacher) {
				this.accountType = this.accountTypes.TeacherIndividual;
				return;
			}
		},
		async handleUpdateUserName() {
			if (!this.user.firstname || !this.user.lastname) {
				return;
			}

			this.loading = true;

			const res = await this.updateName(this.user.firstname, this.user.lastname);
			if (res.message) {
				alert(res.message);
			}

			this.loading = false;
		},
		async handleUpdateUserPassword() {
			if (this.passwordError) {
				return;
			}

			this.loading = true;

			const res = await this.updatePassword(this.user.password);
			if (res.message) {
				alert(res.message);
			}

			this.user.password = '';
			this.user.passwordConfirm = '';

			this.loading = false;
		},
		...mapActions(useUser, ['updateName', 'updatePassword', 'logout']),
	},
});
</script>

<style scoped lang="scss">
// Not rendered at all — see the comment on the field itself. It is there to be
// read by a password manager, not by anyone using the page.
.username-for-password-manager {
	display: none;
}

#hero {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin: 0;
	background-color: rgb(var(--rs-color-brand-honey));
	min-height: 150px;

	* {
		margin: 0;
		color: rgb(var(--rs-color-brand-navy));
	}
}

.pil {
	border: 1px solid rgb(var(--rs-color-brand-grey-light));
	border-radius: var(--rs-border-radius);
	padding: var(--rs-layout-content-padding);
}
</style>
